import { NormalizedProduct } from '@/lib/types/products/normalizedProduct';
import type { RawProduct, ProductVariant as RawProductVariant } from '@/lib/types/products/rawProduct';
import { buildShippingDimensionsFromFields } from '@/lib/functions/resolveProductShippingDimensions';
import { resolveProductUrl } from '@/lib/functions/buildProductBreadcrumbs';

function findVariantForStore(
  variants: RawProductVariant[],
  storeNumber: string | number,
): RawProductVariant | null {
  const store = String(storeNumber);

  return (
    variants.find((v) => String(v?.store?.number) === store)
    ?? variants.find((v) => v.code?.startsWith(`${store}-`))
    ?? null
  );
}

/**
 * Returns true when the raw product has pricing for the requested store.
 */
export const hasVariantForStore = (
  rawProduct: RawProduct,
  storeNumber: string | number,
): boolean => {
  if (!Array.isArray(rawProduct?.variants) || rawProduct.variants.length === 0) {
    return true;
  }

  return findVariantForStore(rawProduct.variants, storeNumber) !== null;
};

/**
 * Normalize the two server payload shapes into the flat SearchProduct
 * shape the card already expects.
 *
 * @param raw          One of the 2 JSON shapes you showed
 * @param storeNumber  Party Supplies ship-from store (e.g. 91)
 */
export const normalizeProduct = (
  rawProduct: RawProduct,
  storeNumber: string | number
): NormalizedProduct => {
  let variant: RawProductVariant | null = null;

  if (Array.isArray(rawProduct?.variants) && rawProduct?.variants.length > 0) {
    variant = findVariantForStore(rawProduct.variants, storeNumber);
  } else if ('pricing' in rawProduct && rawProduct.pricing && 'stock' in rawProduct && rawProduct.stock) {
    variant = {
      code: rawProduct.code,
      pricing: rawProduct.pricing,
      stock: rawProduct.stock,
      taxonomy: rawProduct.taxonomy,
      shippingCategory: rawProduct.shippingCategory || null,
    };
  }

  let inStock = false;
  let stockDetails = null;

  if (variant && variant?.stock?.inStock) {
    inStock = true;
    stockDetails = {
      available: variant?.stock?.details?.available || 0,
      onHold: variant?.stock?.details?.onHold || 0,
      onHand: variant?.stock?.details?.onHand || 0,
    };
  }

  return {
    code: variant?.code || rawProduct.code,
    details: {
      title: rawProduct.details?.title || null,
      description: rawProduct.details?.description || null,
      type: rawProduct.details?.type || null,
      image: rawProduct.details?.image || null,
      attributes: rawProduct.details?.attributes || {},
    },
    pricing: {
      unitPrice: variant?.pricing?.unitPrice || null,
      caseQuantity: variant?.pricing?.caseQuantity || null,
      casePrice: variant?.pricing?.casePrice || null,
      unitPricePromoDiscount: variant?.pricing?.unitPricePromoDiscount || null,
      unitPriceKeyclubDiscount: variant?.pricing?.unitPriceKeyclubDiscount || null,
      casePricePromoDiscount: variant?.pricing?.casePricePromoDiscount || null,
      casePriceKeyclubDiscount: variant?.pricing?.casePriceKeyclubDiscount || null,
    },
    stock: {
      inStock: inStock,
      details: stockDetails,
    },
    taxonomy: variant?.taxonomy || rawProduct.taxonomy || null,
    shippingCategory: variant?.shippingCategory || rawProduct.shippingCategory || null,
    shippingDimensions:
      buildShippingDimensionsFromFields(variant) ??
      rawProduct.shippingDimensions ??
      null,
    url: resolveProductUrl(rawProduct.url, rawProduct.details?.title ?? '', {
      slug: getProductSlugFromUrl(rawProduct.url),
      description: rawProduct.details?.description,
      taxonomy: rawProduct.taxonomy ?? undefined,
    }),
  };
};

function getProductSlugFromUrl(productUrl: string): string {
  return productUrl.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean).at(-1) ?? '';
}
